import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import Logo from '../components/Logo';

function Login({ modoInicial = 'entrar' }) {
  const [modo, setModo] = useState(modoInicial);
  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [resetEnviado, setResetEnviado] = useState(false);
  const [emailNaoConfirmado, setEmailNaoConfirmado] = useState(false);
  const [reenvioEnviado, setReenvioEnviado] = useState(false);
  const timerUsername = useRef(null);

  useEffect(() => {
    if (modo !== 'cadastrar' || !username) { setUsernameStatus(null); return; }
    if (username.length < 3) { setUsernameStatus('short'); return; }
    if (!/^[a-z0-9.]+$/.test(username)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    clearTimeout(timerUsername.current);
    timerUsername.current = setTimeout(async () => {
      const { data } = await supabase
        .from('perfis').select('id').ilike('username', username).maybeSingle();
      setUsernameStatus(data ? 'taken' : 'available');
    }, 400);
    return () => clearTimeout(timerUsername.current);
  }, [username, modo]);

  async function handleSubmit() {
    setErro('');
    if (modo === 'cadastrar') {
      if (!username) { setErro('Escolha um @ de usuário para continuar.'); return; }
      if (usernameStatus !== 'available') { setErro('Escolha um @ válido e disponível antes de continuar.'); return; }
    }
    setCarregando(true);
    try {
      if (modo === 'cadastrar') {
        // Seta antes do signUp para evitar race condition com onAuthStateChange
        localStorage.setItem('nova_conta', '1');
        const { data, error } = await supabase.auth.signUp({
          email,
          password: senha,
          options: {
            data: { nome, username: username || null, whatsapp: whatsapp.replace(/\D/g, '') || null },
            emailRedirectTo: 'https://bibliotecadebolso.app/',
          },
        });
        if (error) {
          localStorage.removeItem('nova_conta');
          if (error.message.includes('Password') || error.message.includes('password')) {
            setErro('A senha deve ter pelo menos 6 caracteres.');
          } else if (error.message.includes('valid email') || error.message.includes('invalid')) {
            setErro('E-mail inválido. Verifique o endereço digitado.');
          } else if (error.message.includes('rate limit') || error.message.includes('too many')) {
            setErro('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
          } else {
            setErro('Não foi possível criar a conta. Tente novamente.');
          }
        } else if (data.user?.identities?.length === 0) {
          // e-mail já cadastrado — Supabase não retorna erro, mas identities fica vazio
          localStorage.removeItem('nova_conta');
          setErro('Este e-mail já está cadastrado. Tente entrar ou recuperar a senha.');
          setCarregando(false);
        } else if (data.session) {
          // confirmação de e-mail desativada — App.js redireciona via onAuthStateChange
        } else {
          // confirmação de e-mail ativada — precisa clicar no link
          setErro('Conta criada! Clique no link que enviamos para ' + email + ' para continuar.');
          setCarregando(false);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
        if (error) {
          if (error.message.includes('not confirmed') || error.message.includes('Email not confirmed')) {
            setEmailNaoConfirmado(true);
            setErro('E-mail ainda não confirmado. Verifique sua caixa de entrada (ou spam).');
          } else {
            setErro('E-mail ou senha incorretos.');
          }
        }
      }
    } catch {
      setErro('Problema de conexão. Verifique sua internet e tente novamente.');
    }
    setCarregando(false);
  }

  async function handleReenviarConfirmacao() {
    if (!email) { setErro('Digite seu e-mail acima para reenviar a confirmação.'); return; }
    setCarregando(true);
    const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: 'https://bibliotecadebolso.app/' } });
    setCarregando(false);
    if (error) setErro('Não foi possível reenviar. Tente novamente em alguns minutos.');
    else { setReenvioEnviado(true); setEmailNaoConfirmado(false); setErro(''); }
  }

  async function handleEsqueciSenha() {
    if (!email) { setErro('Digite seu e-mail acima para redefinir a senha.'); return; }
    setCarregando(true);
    setErro('');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://bibliotecadebolso.app/',
    });
    setCarregando(false);
    if (error) setErro('Não foi possível enviar o e-mail. Verifique o endereço.');
    else setResetEnviado(true);
  }

  function UsernameHint() {
    if (!username) return null;
    if (usernameStatus === 'short') return <span className="text-ink-300">mín. 3 caracteres</span>;
    if (usernameStatus === 'invalid') return <span className="text-red-400">só letras, números e ponto</span>;
    if (usernameStatus === 'checking') return <span className="text-ink-300">verificando…</span>;
    if (usernameStatus === 'taken') return <span className="text-red-400">✗ já em uso</span>;
    if (usernameStatus === 'available') return <span className="text-green-500">✓ disponível</span>;
    return null;
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        <div className="flex justify-center mb-8">
          <button onClick={() => window.location.href = '/'}>
            <Logo size="lg" />
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-line-soft p-6">

          <div className="flex bg-paper rounded-xl p-1 mb-6">
            <button onClick={() => setModo('entrar')}
              className={`flex-1 py-2 text-sm rounded-lg transition-all ${modo === 'entrar' ? 'bg-white text-ink-900 font-medium shadow-sm' : 'text-ink-400'}`}>
              Entrar
            </button>
            <button onClick={() => setModo('cadastrar')}
              className={`flex-1 py-2 text-sm rounded-lg transition-all ${modo === 'cadastrar' ? 'bg-white text-ink-900 font-medium shadow-sm' : 'text-ink-400'}`}>
              Criar conta
            </button>
          </div>

          <div className="flex flex-col gap-3">
            {modo === 'cadastrar' && (
              <>
                <div>
                  <label className="text-xs text-ink-400 mb-1 block">Nome</label>
                  <input type="text" placeholder="Seu nome" value={nome}
                    onChange={e => setNome(e.target.value)}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
                </div>
                <div>
                  <label className="text-xs text-ink-400 mb-1 flex items-center justify-between">
                    <span>@ usuário</span>
                    <UsernameHint />
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-400 pointer-events-none">@</span>
                    <input type="text" placeholder="seunome" value={username}
                      onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9.]/g, ''))}
                      className="w-full border border-line-soft rounded-xl pl-8 pr-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-ink-400 mb-1 block">WhatsApp <span className="text-ink-300">(necessário para empréstimos)</span></label>
                  <input type="tel" placeholder="11999999999" value={whatsapp}
                    onChange={e => setWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
                </div>
              </>
            )}

            <div>
              <label className="text-xs text-ink-400 mb-1 block">E-mail</label>
              <input type="email" placeholder="seu@email.com" value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
            </div>

            <div>
              <label className="text-xs text-ink-400 mb-1 block">Senha</label>
              <input type="password" placeholder="••••••••" value={senha}
                onChange={e => setSenha(e.target.value)}
                className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
            </div>

            {erro && (
              <p className="text-xs text-center text-ink-500 bg-paper rounded-xl px-4 py-3">{erro}</p>
            )}

            <button onClick={handleSubmit} disabled={carregando}
              className="w-full bg-brand text-white py-3 rounded-xl text-sm font-medium mt-2 hover:bg-brand-dark transition-colors disabled:opacity-50">
              {carregando ? 'Aguarde...' : modo === 'entrar' ? 'Entrar' : 'Criar conta'}
            </button>

            {modo === 'entrar' && !resetEnviado && !emailNaoConfirmado && (
              <button type="button" onClick={handleEsqueciSenha} disabled={carregando}
                className="w-full text-xs text-ink-400 hover:text-brand transition-colors py-1">
                Esqueci minha senha
              </button>
            )}
            {emailNaoConfirmado && !reenvioEnviado && (
              <button type="button" onClick={handleReenviarConfirmacao} disabled={carregando}
                className="w-full text-xs text-brand hover:text-brand-dark transition-colors py-1 font-medium">
                Reenviar e-mail de confirmação
              </button>
            )}
            {resetEnviado && (
              <p className="text-xs text-center text-green-600 bg-green-50 rounded-xl px-4 py-3">
                ✓ E-mail de redefinição enviado! Verifique sua caixa de entrada.
              </p>
            )}
            {reenvioEnviado && (
              <p className="text-xs text-center text-green-600 bg-green-50 rounded-xl px-4 py-3">
                ✓ E-mail de confirmação reenviado! Verifique sua caixa de entrada.
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default Login;
